import { useEffect, useState } from "react";
import { usePermissions } from "@/context/PermissionsProvider";
import { useProfileStore } from "@/store/profileStore";
import { usersService } from "@/services/usersService";
import type { UserSummary } from "@/types/entities";

/** Resolves the logged-in user's Atlas {@link UserSummary} (matched by email). */
export function useCurrentUser(legalEntityId?: string | null): {
  currentUser: UserSummary | null;
  loading: boolean;
} {
  const { me } = usePermissions();
  const email = me?.email ?? null;
  const { fetchProfile } = useProfileStore();
  const [currentUser, setCurrentUser] = useState<UserSummary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (email) void fetchProfile(email);
  }, [email, fetchProfile]);

  useEffect(() => {
    if (!email) {
      setCurrentUser(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    usersService
      .list({ legalEntityId: legalEntityId ?? undefined, search: email }, { page: 0, size: 20 })
      .then((res) => {
        if (cancelled) return;
        const normalized = email.trim().toLowerCase();
        const match = (res.content ?? []).find((u) => u.email?.trim().toLowerCase() === normalized);
        setCurrentUser(match ?? null);
      })
      .catch(() => {
        if (!cancelled) setCurrentUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email, legalEntityId]);

  return { currentUser, loading };
}
